import type { AssetDetail } from "@/entities/asset";
import type { AssetTechnicalFormValues } from "@/modules/asset/asset-technical-form";

import { Form } from "antd";
import first from "lodash/first";
import isEqual from "lodash/isEqual";
import { useEffect } from "react";

import {
  getAssetCleanTechnicalData,
  getAssetValidTechnicalData,
  useAssetUpdate,
} from "@/entities/asset";
import { useGetEntityTemplate } from "@/entities/entity-template";
import {
  useAppSaveBar,
  useAppSaveBarHandler,
  useAppSaveBarSync,
} from "@/modules/app-save-bar";

import { ASSET_TECHNICAL_UPDATE_SCOPE } from "./asset-technical-update.constant";

const SCOPE = ASSET_TECHNICAL_UPDATE_SCOPE;

export const useAssetTechnicalDataUpdate = (asset?: AssetDetail) => {
  const [form] = Form.useForm();
  const { setSaveBar } = useAppSaveBar(SCOPE);
  const { mutate, isPending } = useAssetUpdate(asset?.id || 0);
  const initialTechnicalData = getAssetValidTechnicalData(
    asset?.technical_data
  );
  const lastTechnicalUpdate = first(asset?.technical_data_updates || []);

  const { isLoading, schema } = useGetEntityTemplate(
    asset?.asset_type?.entity_template_id ?? 0
  );
  useEffect(() => {
    if (initialTechnicalData) {
      form.setFieldsValue(initialTechnicalData);
    }
  }, [form, initialTechnicalData]);

  const onCancel = () => {
    form.setFieldsValue(initialTechnicalData);
    setSaveBar("dirty", false);
  };

  const handleDataChange = (values: AssetTechnicalFormValues) => {
    const cleanedValues = getAssetCleanTechnicalData(values);
    const isDirty = !isEqual(cleanedValues, initialTechnicalData);
    setSaveBar("dirty", isDirty);
  };

  const onSave = () => {
    const technicalData = getAssetCleanTechnicalData(form.getFieldsValue(true));

    mutate(
      { technical_data: technicalData },
      { onSuccess: () => setSaveBar("dirty", false) }
    );
  };

  // Sync save bar
  useAppSaveBarHandler(SCOPE, { onSave, onCancel });
  useAppSaveBarSync(SCOPE, "loading", isLoading);
  useAppSaveBarSync(SCOPE, "saving", isPending);

  return {
    schema,
    isLoading: isPending || isLoading,
    handleDataChange,
    form,
    lastTechnicalUpdate,
  };
};
